import cx from 'classnames';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../app/hooks';
import { isPlanBooking } from '../util/bookingPricing';
import Badge from '../components/Badge';
import Button from '../components/Button';
import CarViewer from '../components/CarViewer';
import OrderSummary from '../components/OrderSummary';
import ServiceSelector from '../components/ServiceSelector';
import styles from '../sass/components/Services.module.scss';
import { ROUTE_BOOK_SCHEDULE, ROUTE_BOOK_SERVICES } from '../util/constants';
import { additionalFeeNames } from '../constants/discountedFees';
import { isServiceNameKey } from '../util/serviceKeys';
import { goBackTo } from '../util/stepNavigation';
import ServiceAddressSummary from '../components/ServiceAddressSummary';
import { useTenant } from '../components/TenantContext';
import { normalizeThemePreset } from '../util/themePreset';
// Imported last so the CSS bundle's module order is what it was before.
import BookingSummaryBar from '../components/BookingSummaryBar';

export default function Services() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Class hooks for a tenant theme preset's stylesheet (see
  // public/aperio/theme.css, "booking steps"). False for every tenant on the
  // default preset, so their class lists are unchanged.
  const themed = normalizeThemePreset(useTenant()?.themePreset) !== 'default';

  const selectedAddOns = useAppSelector((state) => (
    state.addOns.addOns
  ));
  const selectedServices = useAppSelector((state) => (
    state.serviceTypes.services
  ));

  // Plan bookings have no optional add-ons: send them on to scheduling if
  // they land here via the tab bar or the browser history.
  useEffect(() => {
    if (isPlanBooking(selectedServices)) navigate(ROUTE_BOOK_SCHEDULE);
  }, [selectedServices, navigate]);

  const handleBack = () => {
    goBackTo(navigate, ROUTE_BOOK_SERVICES);
  };

  // What the customer picked: a fee the booking adds by itself (the mileage
  // charge for a far address) is not "1 Add-on selected" (QA #21).
  const chosenAddOnCount = selectedAddOns.filter(
    (addOn) => !(isServiceNameKey(addOn.nameKey)
      && additionalFeeNames.includes(addOn.nameKey)),
  ).length;

  const handleNextClick = () => {
    navigate(ROUTE_BOOK_SCHEDULE);
  };

  return (
    <div className={cx(styles.services, { 'apr-step': themed })}>
      <div className={styles.header}>
        <h1 className={styles.headerDesktop}>{t('addOns.title')}</h1>
        <h4 className={styles.headerMobile}>{t('addOns.title')}</h4>
      </div>
      <div
        className={cx(styles.body, 'container--fluid', {
          'apr-step__body': themed,
        })}
      >
        <div className={cx(styles.bodyLeft, 'container__col-xs-4')}>
          {themed && <ServiceAddressSummary />}
          <div className={styles.carSelectorBox}>
            <CarViewer />
          </div>
          <div className={styles.selectedServicesBox}>
            <OrderSummary onlyServices includeAddOns />
          </div>
        </div>
        <div className={cx(
          styles.bodyRight,
          'container__col-xs-8',
        )}
        >
          <ServiceSelector type="secondary" />
        </div>
      </div>
      <div className={cx(styles.footer, { 'apr-step__footer': themed })}>
        {/* Under the preset the price strip rides in this bar. */}
        {themed && <BookingSummaryBar placement="footer" />}
        <Badge
          variant="secondary"
          className={styles.countBadge}
        >
          {t('addOns.selectedCount', { count: chosenAddOnCount })}
        </Badge>
        <Button
          className={cx(styles.editButton, { 'apr-step__back': themed })}
          onClick={handleBack}
          variant="tertiary"
        >
          {t('common.back')}
        </Button>
        <Button
          className={styles.editButton}
          variant="primary"
          onClick={handleNextClick}
        >
          {t('common.next')}
        </Button>
      </div>
    </div>
  );
}
